/*
 * En error boundary MÅSTE vara en klass — React har ingen hook-motsvarighet till
 * componentDidCatch/getDerivedStateFromError. Projektets funktionella lint-regler
 * stängs därför av i just den här filen, på samma sätt som API-lagret gör.
 */
/* eslint-disable functional/no-class, functional/no-this-expression, no-restricted-syntax */
import { Component, ReactNode } from 'react';
import ErrorView from '@/components/utils/ErrorView';

/**
 * Fångar renderingsfel i rutt-trädet så att EN trasig vy inte släcker hela appen.
 *
 * Utan den här tar ett kastat fel i vilken sida som helst ner React-trädet: skärmen
 * blir vit, navbaren försvinner och enda vägen tillbaka är att ladda om — för en
 * NPF-målgrupp är det skillnaden mellan "den här sidan strular" och "appen är borta".
 * Nu står felet kvar i en läsbar ruta med en väg vidare, och texten säger VAD som
 * gick fel, vilket är det första man behöver veta för att kunna laga det.
 *
 * Fångar även en lazy-laddad rutt som inte går att hämta (ChunkLoadError efter en ny
 * version) — samma symptom, samma hantering.
 *
 * Nyckeln på location gör att gränsen återställs av sig själv vid navigering: annars
 * hade felrutan blivit kvar på NÄSTA sida också, eftersom en error boundary inte
 * återställs av att barnen byts ut.
 */

type Props = {
  children?: ReactNode;
  /** Anropas när användaren väljer att försöka igen. */
  onReset?: () => void;
};

type State = {
  error: Error | null;
};

class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    // Konsolen är den enda platsen felet finns kvar efter att vyn bytts ut.
    console.error('Renderingsfel i rutt-trädet:', error);
  }

  private readonly handleTryAgain = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render() {
    const { error } = this.state;
    if (!error) return <>{this.props.children}</>;

    return <ErrorView error={error.message} onTryAgain={this.handleTryAgain} />;
  }
}

export default RouteErrorBoundary;
